import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { checkAll, checkpProjectRole, checkUserPlan, checkWorkspaceRole, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ProjectRole } from '../../models/userRole.model';
import { IUserService } from '../user/user.service';
import { Plan } from '../user/user.store';
import { WorkspaceRole } from '../workspaces/workspaces.store';

interface IAnnotationPermissions {
  canGoBackToProject(): IPermissionCheckResult;
  canSeeAnnotateButton(projectId: string): IPermissionCheckResult;
  canSeeReviewButton(projectId: string): IPermissionCheckResult;
  canAnnotate(projectId: string): IPermissionCheckResult;
  canReview(projectId: string): IPermissionCheckResult;
  canDiscard(): IPermissionCheckResult;
}

export const useAnnotationPermissions = (): IAnnotationPermissions => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const userService = useInjection(IUserService.$);
  const currentWorkspace = currentWorkspaceStore.currentWorkspace;
  const { t } = useTranslator();

  const canGoBackToProject = (): IPermissionCheckResult => {
    return checkWorkspaceRole(
      [
        WorkspaceRole.Owner,
        WorkspaceRole.Manager,
        WorkspaceRole.Developer,
        WorkspaceRole.Trainer,
        WorkspaceRole.Collaborator,
        WorkspaceRole.Assistant,
        WorkspaceRole.Guest,
      ],
      currentWorkspace,
      t
    );
  };

  const canSeeReviewButton = (projectId: string): IPermissionCheckResult => {
    const currentRole = userService.getUserRoleInProject(projectId);

    let result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    if (result.allowed) {
      result = checkpProjectRole([ProjectRole.Annotator, ProjectRole.Reviewer], currentRole, t);
    }

    return result;
  };

  const canSeeAnnotateButton = (projectId: string): IPermissionCheckResult => {
    const currentRole = userService.getUserRoleInProject(projectId);

    let result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    if (result.allowed) {
      result = checkpProjectRole([ProjectRole.Annotator, ProjectRole.Reviewer], currentRole, t);
    }

    return result;
  };

  const canAnnotate = (projectId: string): IPermissionCheckResult => {
    const currentRole = userService.getUserRoleInProject(projectId);

    let result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    if (result.allowed) {
      result = checkpProjectRole([ProjectRole.Annotator, ProjectRole.Reviewer], currentRole, t);
    }

    return result;
  };

  const canReview = (projectId: string): IPermissionCheckResult => {
    const currentRole = userService.getUserRoleInProject(projectId);

    let result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    if (result.allowed) {
      result = checkpProjectRole([ProjectRole.Reviewer], currentRole, t);
    }

    return result;
  };

  const canDiscard = (): IPermissionCheckResult => {
    return checkAll([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
  };

  return {
    canGoBackToProject,
    canSeeAnnotateButton,
    canSeeReviewButton,
    canAnnotate,
    canReview,
    canDiscard,
  };
};
